<template>
  <div class="category-tree-item">
    <div :class="[
        'flex items-center p-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 relative group',
        selectedId === category.id ? 'bg-primary/10 border-l-4 border-primary pl-1' : '',
        category.hasPromptChanges ? 'border-l-4 border-red-400 pl-1' : (hasChangesInChildren && !isExpanded ? 'border-l-4 border-orange-400 pl-1' : ''),
        hasChildren ? 'font-medium' : '',
        category.level === 1 ? 'mb-1' : '',
      ]" @click="handleSelectCategory(category)" :title="statusTooltip">

      <!-- Alt kategorilerde değişiklik olduğunda gösterilecek info tooltip KALDIRILDI -->

      <button v-if="hasChildren" class="w-6 h-6 flex items-center justify-center mr-1 relative hover:bg-orange-100 rounded-full" @click.stop="toggleExpand" @mouseover="showChevronTooltip = hasChangesInChildren && !isExpanded" @mouseleave="showChevronTooltip = false">
        <span class="material-symbols-outlined transition-transform duration-300" :class="[{ 'rotate-90': isExpanded },
                       hasChangesInChildren && !isExpanded ? 'text-orange-500' : 'text-gray-500']">
          chevron_right
        </span> <span v-if="hasChangesInChildren && !isExpanded" class="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>

        <!-- Tooltip sadece chevron üzerinde hover olunca görünür -->
        <div v-if="showChevronTooltip" class="absolute left-6 top-1/2 transform -translate-y-1/2 bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-md border border-orange-300 shadow-sm whitespace-nowrap z-10">
          <div class="flex items-center gap-1.5">
            <span class="material-symbols-outlined !text-[12px]">warning</span> <span>Alt kategorilerde değişiklik var!</span>
          </div>
        </div>
      </button>
      <span v-else class="w-6 mr-1" :class="{'ml-6': category.level > 2}"></span>

      <span class="material-symbols-outlined mr-2 relative" :class="[category.hasPromptChanges ? 'text-red-500' :
                    (hasChangesInChildren && !isExpanded ? 'text-orange-500' : 'text-gray-500'), 
                     category.level > 1 ? 'text-sm' : '']">
        {{ hasChildren ? (isExpanded ? 'folder_open' : 'folder') : 'description' }}
        <span v-if="category.hasPromptChanges" class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
      </span>

      <div class="flex-grow truncate flex items-center gap-1" :class="{
        'text-red-600 font-medium': category.hasPromptChanges,
        'text-orange-600 font-medium': hasChangesInChildren && !isExpanded && !category.hasPromptChanges
      }">
        {{ category.name }}
        <div v-if="category.hasPromptChanges" class="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
      </div>

      <span v-if="category.is_custom" class="px-2 py-0.5 ml-2 text-xs rounded-full bg-primary/10 text-primary">
        Özel
      </span>
    </div>

    <div v-if="isExpanded && hasChildren" class="ml-4 pl-2 mt-1 border-l border-gray-200">
      <CategoryTreeItem v-for="child in category.children" :key="child.id" :category="child" :selected-id="selectedId" :expanded-categories="expandedCategories" @select="handleChildSelect"/>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

// Props
const props = defineProps({
  category: {
    type: Object,
    required: true,
  },
  selectedId: {
    type: [Number, String],
    default: null,
  },
  // Dışarıdaki expanded kategoriler listesi (arama sonuçları için)
  expandedCategories: {
    type: [Set, Array, Object],
    default: () => new Set(),
  },
})

// Emits
const emit = defineEmits(['select'])

// State
const isExpanded = ref(false)
const showChevronTooltip = ref(false)

// Alt kategorilerde değişiklik olup olmadığını kontrol et
const hasChangesInChildren = computed(() => {
  if (!props.category.children || !props.category.children.length) return false

  // Recursive olarak tüm alt kategorileri kontrol et
  const checkChildrenForChanges = (children) => {
    return children.some(child => {
      // Bu kategoride değişiklik var mı?
      if (child.hasPromptChanges) return true

      // Alt kategorilerde değişiklik var mı?
      if (child.children && child.children.length) {
        return checkChildrenForChanges(child.children)
      }

      return false
    })
  }

  return checkChildrenForChanges(props.category.children)
})

// Genişletilmiş durumu izle - Set yerine array olarak gelen prop'u destekle
watch(() => props.expandedCategories, (newVal) => {
  const checkExpanded = () => {
    if (!props.expandedCategories) return false

    // Set olarak geldiyse
    if (typeof props.expandedCategories.has === 'function') {
      return props.expandedCategories.has(props.category.id)
    }

    // Array olarak geldiyse (veya başka bir tip)
    if (Array.isArray(props.expandedCategories)) {
      return props.expandedCategories.includes(props.category.id)
    }

    return false
  }

  if (checkExpanded()) {
    isExpanded.value = true
  }
}, { immediate: true })

// Başlangıçta arama sonuçlarını göstermek için
onMounted(() => {
  const checkExpanded = () => {
    if (!props.expandedCategories) return false

    // Set olarak geldiyse
    if (typeof props.expandedCategories.has === 'function') {
      return props.expandedCategories.has(props.category.id)
    }

    // Array olarak geldiyse (veya başka bir tip)
    if (Array.isArray(props.expandedCategories)) {
      return props.expandedCategories.includes(props.category.id)
    }

    return false
  }

  if (checkExpanded()) {
    isExpanded.value = true
  }
})

// Computed
const hasChildren = computed(() => {
  return props.category.children && props.category.children.length > 0
})

// Açıklama metni oluştur
const statusTooltip = computed(() => {
  if (props.category.hasPromptChanges) {
    return 'Bu kategoride değişiklik yapıldı'
  } else if (hasChangesInChildren && !isExpanded.value) {
    return 'Alt kategorilerde değişiklik var! Açmak için tıkla'
  }
  return ''
})

// Methods
const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
}

const handleSelectCategory = (category) => {
  emit('select', category)
}

const handleChildSelect = (category) => {
  emit('select', category)
}
</script>

<style scoped>
.category-tree-item {
  margin-bottom: 2px;
}
</style>